





















import {Component, Mixins, Prop} from 'vue-property-decorator'
import Translation from '@/mixins/Translation'
import PopupHandler from '@/mixins/popupHandler'
import Date from '@/pages/issue/Date.vue'
import Client from '@/pages/issue/Client.vue'
import ClientContact from '@/pages/issue/ClientContact.vue'
import Items from '@/pages/issue/Items.vue'
import Confirm from '@/pages/issue/Confirm.vue'
import BizInfo from '@/pages/issue/BizInfo.vue'
import PopbillCertRegistrationPopup from '@/components/popup/btt/PopbillCertRegistration.vue'
import IssueState from '@/pages/issue/state'
import {Invoice} from '@/store/modules/invoices/modules/issued/types'
import {ExtraData} from '@/store/modules/issue/types'
import {LogEventWidthGroupsPayload} from '@/store/modules/gtm/types'
import {Status} from '@/store/modules/institutions/types'
import {namespace} from 'vuex-class'

const issue = namespace('issue')
const issuedInvoices = namespace('invoices/issued')
const layout = namespace('layout')
const gtm = namespace('gtm')
const institutions = namespace('institutions')

@Component({
  components: {
    Date, Client, ClientContact, Items, Confirm, BizInfo,
    PopbillCertRegistrationPopup,
  },
})
export default class Issue extends Mixins(Translation, PopupHandler) {
  @Prop({default: 0, type: Number}) windowScrollY: number
  @Prop({default: 'terms', type: String}) translation: string

  issueState: IssueState = IssueState.DATE
  transitionName: string = ''
  loading: boolean = true
  done: boolean = false

  @issue.State('copyInvoiceId') copyInvoiceId: string
  @issue.State('writeDate') writeDate: string
  @issue.State('clientId') clientId: string
  @issue.State('items') items: object[]
  @issue.State('extraData') extraData: ExtraData
  @issue.Getter('gtmCommonInfo') gtmCommonInfo: object
  @issue.Mutation('clear') clear: () => void
  @issue.Mutation('update') update: (payload: any) => void

  @issuedInvoices.State('invoice') invoice: Invoice
  @issuedInvoices.Action('getInvoice') getInvoice: (payload: {id: string}) => Promise<any>

  @layout.Mutation('updateError') updateError: (payload: {id, message}) => void
  @layout.Mutation('updateToast') updateToast: (payload: {id, message}) => void
  @layout.Mutation('updateRouting') updateRouting: (payload: boolean) => void

  @gtm.Action('logEventWithGroups') logEventWithGroups: (payload: LogEventWidthGroupsPayload) => void

  @institutions.Action('getCertInstitutions') getCertInstitutions
  @institutions.Getter('popbillStatus') popbillStatus: Status

  get currentPage(): string {
    return this.issueState.pageName
  }

  get transition(): string {
    return this.transitionName
  }

  get isConfirm(): boolean {
    return this.issueState.isConfirm
  }

  get isEnteredIssueData(): boolean {
    return Boolean(this.writeDate || this.clientId || this.items.length)
  }

  get confirmMessageOptions() {
    const {ts} = this
    return {
      message: `작성 중인 ${ts('invoice')} ${ts('info')}가 사라집니다. <br>계속 진행 하시겠습니까?`,
      options: {
        showClose: false,
        confirmButtonText: `${ts('confirm')}`,
        cancelButtonText: `${ts('cancel')}`,
        dangerouslyUseHTMLString: true,
      }
    }
  }

  created() {
    this.loadPopbillStatus()
    if(this.copyInvoiceId) {
      this.setCopiedIssueData()
    } else {
      this.loading = false
    }
  }

  beforeRouteUpdate(to, from, next) {
    this.handleModifiedConfirm(to, from, next)
  }

  beforeRouteLeave(to, from, next) {
    this.handleModifiedConfirm(to, from, next)
  }

  loadPopbillStatus() {
    this.getCertInstitutions()
      .then(() => {
        if(this.popbillStatus !== 'success') {
          this.openPopup('popbillCert')
        }
      })
      .catch((error) => {
        const {ts} = this
        this.updateError({
          id: 'get-popbill-status',
          message: `${ts('issue')} 가능한 ${ts('certificate')}를 조회하는데 ${ts('failed')}하여 ${ts('home')}으로 이동합니다.`,
        })
        this.$router.push({name: 'home'}).catch((e) => e)
        throw error
      })
  }

  handleModifiedConfirm(to, from, next) {
    if(this.isEnteredIssueData && !this.done) {
      const {message, options} = this.confirmMessageOptions
      this.$confirm(message, options)
        .then(() => {
          this.clear()
          next()
          if(from.name === to.name) {
            this.resetIssueState()
          }
        })
        .catch((e) => {
          this.updateRouting(false)
          return e
        })
    } else {
      this.clear()
      next()
    }
  }

  resetIssueState() {
    this.issueState = IssueState.DATE
  }

  transformInvoiceToIssueData(invoice) {
    return {
      clientId: invoice?.clientId || '',
      items: invoice?.items.map((item) => ({
        ...item,
        purchaseDate: '',
        taxFree: item.taxType === '면세',
        totalAmount: item.supplyCost + item.tax
      })) || [],
      extraData: {
        purposeType: invoice?.purposeType || '청구',
        cash: invoice?.cash || 0,
        checkBill: invoice?.checkBill || 0,
        credit: invoice?.credit || 0,
        note: invoice?.note || 0,
        remark: invoice?.remark || '',
      }
    }
  }

  setCopiedIssueData() {
    this.getInvoice({id: this.copyInvoiceId})
      .then(() => {
        this.update(this.transformInvoiceToIssueData(this.invoice))
        this.issueState = IssueState.CONFIRM
        const {ts} = this
        this.updateToast({
          id: 'update-recent-info',
          message: `${ts('my')} ${ts('biz')}, ${ts('client')} ${ts('info')}는 최신 ${ts('info')}로 ${ts('input')}되었습니다.`,
        })
        this.logEventWithGroups({
          eventType: 'copy_invoice',
          eventProperties: {
            ...this.gtmCommonInfo,
            'status': this.extraData.purposeType,
            'invoice_duplicated': true,
          }
        })
      })
      .catch((error) => {
        this.clear()
        this.updateError({
          id: 'get-invoice',
          message: `${this.ts('invoice')} 정보를 가져오는데 ${this.ts('failed')}하였습니다.`,
        })
        throw error
      })
      .finally(() => {
        this.loading = false
      })
  }

  handleAction(from, action) {
    this.issueState[action](this)
  }

  onCertRegister() {
    this.$router
      .push({
        name: 'cert-center',
        query: {
          openPopupName: 'certRegister',
        }
      })
      .catch((err) => err)
  }
}
